import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import {
  Package,
  Printer,
  Hand,
  PackageCheck,
  Truck,
  Clock,
  Droplet,
  Box,
  RotateCcw,
  AlertCircle,
  Store,
  XCircle,
  ArrowUpRight,
  Pause,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DisplayStatus } from '../types';

interface ProductionStatusBadgeProps {
  status: DisplayStatus | string | null;
  /** Bin number to display when status is BINNED */
  binNumber?: number | null;
  /** Batch number to display when status is IN_PRODUCTION */
  batchNumber?: number | null;
  className?: string;
  /**
   * Fit a fixed-width cell (the Order Queue's Status column): the label
   * truncates with an ellipsis and the full label is the hover title.
   */
  truncate?: boolean;
  /**
   * Hover title. Left out: the full label when `truncate`, else none. `null`:
   * none, for a badge that triggers its own popover.
   */
  title?: string | null;
}

const statusConfig: Record<DisplayStatus, {
  labelKey: string;
  variant: 'pending' | 'processing' | 'info' | 'success' | 'warning' | 'destructive';
  icon: typeof Package;
}> = {
  NEW: {
    labelKey: 'orders.status.ready',
    variant: 'success',
    icon: Clock
  },
  PICKING: {
    labelKey: 'orders.status.picking',
    variant: 'info',
    icon: Package
  },
  PRETREAT: {
    labelKey: 'orders.status.pretreat',
    variant: 'info',
    icon: Droplet
  },
  PRINTING: {
    labelKey: 'orders.status.printing',
    variant: 'info',
    icon: Printer
  },
  CATCHING: {
    labelKey: 'orders.status.catching',
    variant: 'info',
    icon: Hand
  },
  BINNED: {
    labelKey: 'orders.status.binned',
    variant: 'warning',
    icon: Box
  },
  PACKING: {
    labelKey: 'orders.status.packing',
    variant: 'info',
    icon: PackageCheck
  },
  SHIPPED: {
    labelKey: 'orders.status.shipped',
    variant: 'success',
    icon: Truck
  },
  REDO: {
    labelKey: 'orders.status.redo',
    variant: 'destructive',
    icon: RotateCcw
  },
  OOS: {
    labelKey: 'orders.status.oos',
    variant: 'destructive',
    icon: AlertCircle
  },
  READY_FOR_PICKUP: {
    labelKey: 'orders.status.readyForPickup',
    variant: 'success',
    icon: Store
  },
  CANCELLED: {
    labelKey: 'orders.status.cancelled',
    variant: 'destructive',
    icon: XCircle
  },
  ON_HOLD: {
    labelKey: 'orders.status.onHold',
    variant: 'warning',
    icon: Pause
  },
  EXTERNALLY_FULFILLED: {
    labelKey: 'orders.status.extFulfilled',
    variant: 'warning',
    icon: ArrowUpRight
  },
  // Live garment units already cover the order even though its prod_orders row
  // still reads NEW. Info blue, like the other active-processing stages: the
  // work is under way somewhere, it is not an error and nothing is waiting.
  IN_PRODUCTION: {
    labelKey: 'orders.status.inProduction',
    variant: 'info',
    icon: Printer
  }
};

/**
 * ProductionStatusBadge - Shows the workflow stage for orders.
 *
 * Uses semantic colors to indicate workflow progress:
 * - Success (green): Ready to batch, Shipped
 * - Info (blue): Active processing stages (Picking, Pretreat, Printing, Catching, Packing)
 * - Warning (amber): Waiting state (Binned - waiting for other items)
 * - Destructive (red): Issues requiring attention (OOS, Redo)
 *
 * Used on Orders page to show where an order is in production.
 */
export function ProductionStatusBadge({ status, binNumber, batchNumber, className, truncate = false, title }: ProductionStatusBadgeProps) {
  const { t } = useTranslation('production');
  const titleFor = (label: string) => (title === undefined ? (truncate ? label : undefined) : title ?? undefined);
  // If no prod_order exists, order hasn't entered production yet - show as Ready (green)
  if (!status) {
    const readyLabel = t('orders.status.ready');
    return (
      <Badge
        variant="success"
        className={cn("gap-1.5", truncate && "min-w-0 max-w-full", className)}
        title={titleFor(readyLabel)}
      >
        <Clock className="h-3 w-3 shrink-0" />
        {truncate ? <span className="truncate">{readyLabel}</span> : readyLabel}
      </Badge>
    );
  }

  const config = statusConfig[status as DisplayStatus];
  if (!config) {
    // Handle unknown status gracefully
    return (
      <Badge variant="muted" className={className}>
        {status}
      </Badge>
    );
  }

  const Icon = config.icon;

  // Special case: show bin number for BINNED status, batch number for
  // IN_PRODUCTION (same pattern: the number is the whole point of the badge).
  const label = status === 'BINNED' && binNumber
    ? t('orders.status.binnedNumber', { number: binNumber })
    : status === 'IN_PRODUCTION' && batchNumber
      ? t('orders.status.inProductionBatch', { number: batchNumber })
      : t(config.labelKey);

  return (
    <Badge
      variant={config.variant}
      className={cn("gap-1.5", truncate && "min-w-0 max-w-full", className)}
      title={titleFor(label)}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {truncate ? <span className="truncate">{label}</span> : label}
    </Badge>
  );
}
